import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { useUploadFailureDialog } from "../upload-progress/useUploadFailureDialog";
import { useS3Upload, type ValidationError } from "../../hooks/useS3Upload";
import type { SoremonAttachmentInput } from "../../app/services/soremon";

/* BE（soremon/attachments.py）が読み取れる形式・件数・サイズに合わせる。 */
export const ACCEPT_FILE_TYPES = [
  ".png", ".jpg", ".jpeg", ".webp", ".gif",
  ".pdf",
  ".txt", ".md", ".markdown", ".csv", ".tsv", ".json", ".xml", ".html", ".htm",
  ".log", ".yaml", ".yml", ".srt", ".vtt", ".lrc",
  ".docx", ".xlsx", ".pptx",
  ".mp3", ".wav", ".m4a", ".aac", ".flac", ".ogg",
];
const MAX_FILES = 5;
const MAX_FILE_SIZE = 200 * 1024 * 1024;
/** 全件アップロードが終わってから進捗表示を畳むまでの時間（DM と同じ） */
const CLEAR_COMPLETED_MS = 1500;

/**
 * soremon への添付ファイル。CommentField で選んだファイルを、送信時に
 * purpose=soremon_attachment で S3 へアップロードする（register はしない）。
 * 失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログと進捗表示は DM と同じ部品を使う。
 */
export const useSoremonAttachments = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const {
    uploadFiles,
    uploadingFiles,
    abortFile,
    clearCompleted,
    retryFile,
    retryFiles,
    postProcessLabel,
  } = useS3Upload({
    purpose: "soremon_attachment",
    skipRegister: true,
    restrictions: { accept: ACCEPT_FILE_TYPES, maxFiles: MAX_FILES, maxSize: MAX_FILE_SIZE },
    onValidationError: (errors: ValidationError[]) => {
      errors.forEach((error) => toast.error(error.message));
    },
  });
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({ retryFiles });
  const uploading = uploadingFiles.some((f) => f.status === "uploading" || f.status === "pending");

  useEffect(() => {
    if (uploadingFiles.length === 0) return;
    if (uploadingFiles.every((f) => f.status === "completed")) {
      const timer = setTimeout(clearCompleted, CLEAR_COMPLETED_MS);
      return () => clearTimeout(timer);
    }
  }, [uploadingFiles, clearCompleted]);

  const addFiles = useCallback((files: File[]) => {
    setSelectedFiles((prev) => {
      const next = [...prev, ...files];
      if (next.length > MAX_FILES) toast.error(`添付は ${MAX_FILES} 件までです`);
      return next.slice(0, MAX_FILES);
    });
  }, []);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * `files` をアップロードし、送信に使う添付を返す。失敗ダイアログでキャンセル
   * されたら null を返し、選択を元に戻す。
   */
  const upload = useCallback(
    async (files: File[]): Promise<SoremonAttachmentInput[] | null> => {
      if (files.length === 0) return [];
      setSelectedFiles([]);
      const resolved = await resolveUploadFailures(await uploadFiles(files));
      if (resolved === null) {
        clearCompleted();
        setSelectedFiles(files);
        return null;
      }
      return resolved
        .filter((f) => f.fileKey)
        .map((f) => ({ file_key: f.fileKey as string, file_name: f.file.name }));
    },
    [uploadFiles, resolveUploadFailures, clearCompleted],
  );

  return {
    selectedFiles,
    /** 送信に失敗したとき、選んでいたファイルを入力欄へ戻す */
    restoreFiles: setSelectedFiles,
    addFiles,
    removeFile,
    upload,
    uploading,
    uploadingFiles,
    abortFile,
    retryFile,
    postProcessLabel,
    uploadFailureDialog,
  };
};
